import {Department, Palette} from 'hubbl-shared';
import {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {AssetIconChevron} from '../../assets/icons/icon-chevron/AssetIconChevron';
import {TextHeading7} from '../text/TextHeading7';
import {TextBodySmall} from '../text/TextBodySmall';
import {useI18n} from '../../hooks';

interface DepartmentMultiSelectProps {
  existingDepartments: Department[];
  selectedDepartments: Department[];
  onChange: (departments: Department[]) => void;
  placeholder?: string;
}

const Container = styled.div`
  width: 100%;
`;

const SelectedPillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  min-height: 32px;
`;

const SelectedPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${Palette.almostBlack};
  color: ${Palette.white};
  border-radius: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${Palette.almostBlack}dd;
    transform: translateY(-1px);
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${Palette.white};
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  margin-left: 4px;

  &:hover {
    opacity: 1;
    scale: 1.1;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  padding-right: 40px;
  border: 1px solid ${Palette.white};
  border-radius: 8px;
  color: ${Palette.almostBlack};
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Poppins';
  background-color: ${Palette.primarySubtleGreen};

  &::placeholder {
    color: ${Palette.almostBlack500};
  }

  &:focus {
    outline: none;
    border-color: ${Palette.primaryTerraCotta};
    box-shadow: 0 0 0 2px rgba(224, 71, 48, 0.1);
  }
`;

const ChevronContainer = styled.div<{isOpen: boolean}>`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  transform: ${({isOpen}) => (isOpen ? 'rotate(-90deg)' : 'rotate(90deg)')};
`;

const SuggestionsList = styled.ul<{top: number; left: number; width: number}>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  width: ${props => props.width}px;
  padding: 0;
  margin: 0;
  margin-top: 4px;
  list-style: none;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999;
  cursor: pointer;
`;

const SuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${Palette.primaryCherokee};
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const InfoMessage = styled.div`
  margin-top: 8px;
`;

export const DepartmentMultiSelect = ({
  existingDepartments,
  selectedDepartments,
  onChange,
  placeholder,
}: DepartmentMultiSelectProps) => {
  const t = useI18n();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [shouldFilterOnInput, setShouldFilterOnInput] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isSelectingRef = useRef(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const displayPlaceholder = placeholder || t.common.selectOrTypeDepartments;

  // Filter out already selected departments
  const availableDepartmentLabels = useMemo(() => {
    const selectedIds = new Set(selectedDepartments.map(d => d.id));
    return existingDepartments
      .filter(dept => !selectedIds.has(dept.id))
      .map(dept => dept.label.en || dept.label.da || '')
      .filter(label => label.trim() !== '');
  }, [existingDepartments, selectedDepartments]);

  // Filter suggestions based on input
  const filteredSuggestions = useMemo(() => {
    if (!inputValue.trim()) {
      return availableDepartmentLabels;
    }
    const lowercaseInput = inputValue.toLowerCase();
    return availableDepartmentLabels.filter(label =>
      label.toLowerCase().includes(lowercaseInput),
    );
  }, [inputValue, availableDepartmentLabels]);

  const updateDropdownPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (!showSuggestions) return;
    updateDropdownPosition();

    const handleScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showSuggestions]);

  const handleRemoveDepartment = (department: Department) => {
    onChange(selectedDepartments.filter(d => d.id !== department.id));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShouldFilterOnInput(true);
    setShowSuggestions(true);
    updateDropdownPosition();
  };

  const handleAddDepartment = (department: Department) => {
    // Check if already selected
    if (selectedDepartments.some(d => d.id === department.id)) {
      return;
    }
    onChange([...selectedDepartments, department]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    isSelectingRef.current = true;
    setInputValue('');

    // Find the existing department
    const existingDept = existingDepartments.find(
      dept => dept.label.en === suggestion || dept.label.da === suggestion,
    );

    if (existingDept) {
      handleAddDepartment(existingDept);
    }

    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (isSelectingRef.current) {
        return;
      }
      setShowSuggestions(false);
      setInputValue('');
    }, 200);
  };

  const handleInputClick = () => {
    setShouldFilterOnInput(false);
    setShowSuggestions(true);
    updateDropdownPosition();
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isSelectingRef.current = true;
    setShowSuggestions(prev => {
      const newValue = !prev;
      if (newValue) {
        setTimeout(() => {
          updateDropdownPosition();
          inputRef.current?.focus();
        }, 0);
      }
      return newValue;
    });
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  return (
    <Container>
      {selectedDepartments.length > 0 && (
        <SelectedPillsContainer>
          {selectedDepartments.map(department => (
            <SelectedPill key={department.id}>
              <TextHeading7
                text={
                  department.label.en || department.label.da || department.id
                }
                color={Palette.white}
              />
              <RemoveButton
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveDepartment(department);
                }}>
                ✕
              </RemoveButton>
            </SelectedPill>
          ))}
        </SelectedPillsContainer>
      )}

      <InputWrapper>
        <InputContainer>
          <StyledInput
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputClick}
            onClick={handleInputClick}
            onBlur={handleBlur}
            placeholder={displayPlaceholder}
            ref={inputRef}
          />
          <ChevronContainer
            isOpen={showSuggestions}
            onMouseDown={handleChevronClick}>
            <AssetIconChevron size={16} color={Palette.almostBlack500} />
          </ChevronContainer>
        </InputContainer>

        {showSuggestions &&
          (shouldFilterOnInput
            ? filteredSuggestions
            : availableDepartmentLabels
          ).length > 0 &&
          createPortal(
            <SuggestionsList
              top={dropdownPosition.top}
              left={dropdownPosition.left}
              width={dropdownPosition.width}>
              {(shouldFilterOnInput
                ? filteredSuggestions
                : availableDepartmentLabels
              ).map((suggestion, index) => (
                <SuggestionItem
                  key={index}
                  onMouseDown={e => {
                    e.preventDefault();
                    handleSuggestionClick(suggestion);
                  }}>
                  <TextHeading7 text={suggestion} color={Palette.almostBlack} />
                </SuggestionItem>
              ))}
            </SuggestionsList>,
            document.body,
          )}
      </InputWrapper>
      {selectedDepartments.length === 0 && (
        <InfoMessage>
          <TextBodySmall
            text={t.common.ifNoDepartmentsSelectedEveryoneInvited}
            color={Palette.almostBlack500}
          />
        </InfoMessage>
      )}
      {selectedDepartments.length > 0 &&
        selectedDepartments.length === existingDepartments.length && (
          <InfoMessage>
            <TextBodySmall
              text={t.common.allDepartmentsSelectedEveryoneInvited}
              color={Palette.almostBlack500}
            />
          </InfoMessage>
        )}
    </Container>
  );
};
